﻿import {hasFlag, StringNamedValue, translation} from "@2d-front-monorepo/core";
import {IMvvmContextState, MvvmSelect, MvvmTextInput} from "@2d-front-monorepo/design-library";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, useCallback, useEffect, useMemo} from "react";
import {Addresses} from "../Addresses";
import ResidencyPropertyFlags from "../enums/ResidencyPropertyFlags";
import {useFieldValidation} from "../helpers/useFieldValidation";
import ICreateComponentAttachments from "../interfaces/ICreateComponentAttachments";
import {Address} from "../types/Address";
import {CreateResidencyArguments} from "../types/arguments/CreateResidencyArguments";
import {EntityProfile} from "../types/InstallationByHostData";
import {IntegraZone} from "../types/IntegraZone";
import {ComponentWithChildrenParts} from "./ComponentParts";
import FullCreateAttachmentsComponent from "./FullCreateAttachmentsComponent";

export interface CreateResidencyComponentProps extends ComponentWithChildrenParts<CreateResidencyModel> {
	autoFocus?: boolean;
	hideBody?: boolean;
	configuration: EntityProfile<ResidencyPropertyFlags>;
	context: IMvvmContextState<CreateResidencyModel>;
	zones: IntegraZone[];
	provinces: StringNamedValue[];
	cities: StringNamedValue[];
	onAddressBlur?: (address: Address) => void;
}

export interface CreateResidencyModel extends ICreateComponentAttachments {
	arguments: CreateResidencyArguments;
	customData: Record<string, any>;
}

export default function CreateResidencyComponent(props: CreateResidencyComponentProps): JSX.Element {
	const {
		context,
		zones,
		provinces,
		cities,
		configuration,
		autoFocus,
		hideBody,
		header,
		children,
		footer,
		onAddressBlur
	} = props;

	const readOnlyFields = configuration.fieldRules.readOnly || ResidencyPropertyFlags.None;
	const hiddenFields = configuration.fieldRules.hidden || ResidencyPropertyFlags.None;
	const freeFormCity = cities.length === 0;
	const freeFormState = provinces.length === 0;

	const zoneItems: StringNamedValue[] = useMemo(() => {
		return zones.map(z => {
			return {label: z.name, value: z.id};
		});
	}, [zones]);

	useEffect(() => {
		if (zones.length === 1) {
			context.setValue("arguments.zoneId", zones[0].id);
		}
	}, [zones]);

	const handleAddressBlur = useCallback(() => {
		if (onAddressBlur == null) return;
		const address = context.state.arguments.address;
		if (Addresses.hasMinimalData(address)) {
			onAddressBlur(address);
		}
	}, [context, onAddressBlur]);

	const streetNumberValidation = useFieldValidation<ResidencyPropertyFlags, string>(configuration.fieldRules, ResidencyPropertyFlags.StreetNumber, "residency", "street-number");
	const streetValidation = useFieldValidation<ResidencyPropertyFlags, string>(configuration.fieldRules, ResidencyPropertyFlags.Street, "residency", "street");
	const unitValidation = useFieldValidation<ResidencyPropertyFlags, string>(configuration.fieldRules, ResidencyPropertyFlags.Unit, "residency", "unit");
	const suffixValidation = useFieldValidation<ResidencyPropertyFlags, string>(configuration.fieldRules, ResidencyPropertyFlags.Suffix, "residency", "suffix");
	const postalCodeValidation = useFieldValidation<ResidencyPropertyFlags, string>(configuration.fieldRules, ResidencyPropertyFlags.PostalCode, "residency", "postal-code");
	const cityValidation = useFieldValidation<ResidencyPropertyFlags, string>(configuration.fieldRules, ResidencyPropertyFlags.City, "residency", "city");
	const stateValidation = useFieldValidation<ResidencyPropertyFlags, string>(configuration.fieldRules, ResidencyPropertyFlags.State, "residency", "state");
	const zoneValidation = useFieldValidation<ResidencyPropertyFlags, string>(configuration.fieldRules, ResidencyPropertyFlags.Zone, "residency", "zone");

	return (<div className="layout vertical gap">
		{header?.(context.state)}
		{!hideBody &&
            <div className="grid">
                <MvvmTextInput context={context}
                               property="arguments.address.streetNumber"
                               type="text"
                               column="col-1of4"
                               autoComplete={"off"}
                               autoFocus={autoFocus}
                               hidden={hasFlag(hiddenFields, ResidencyPropertyFlags.StreetNumber)}
                               disabled={hasFlag(readOnlyFields, ResidencyPropertyFlags.StreetNumber)}
                               validation={streetNumberValidation.validation}
                               transform={streetNumberValidation.transform}
                               minLength={streetNumberValidation.minLength}
                               maxLength={streetNumberValidation.maxLength}
                               onBlur={handleAddressBlur}
                               label={translation(PermitTranslations.new_residency__street_number)}/>

                <MvvmTextInput context={context}
                               property="arguments.address.street"
                               type="text"
                               column="col-1of4"
                               autoComplete={"address-line1"}
                               hidden={hasFlag(hiddenFields, ResidencyPropertyFlags.Street)}
                               disabled={hasFlag(readOnlyFields, ResidencyPropertyFlags.Street)}
                               validation={streetValidation.validation}
                               transform={streetValidation.transform}
                               minLength={streetValidation.minLength}
                               maxLength={streetValidation.maxLength}
                               onBlur={handleAddressBlur}
                               label={translation(PermitTranslations.new_residency__street)}/>

                <MvvmTextInput context={context}
                               property="arguments.address.unit.unit"
                               type="text"
                               column="col-1of4"
                               hidden={hasFlag(hiddenFields, ResidencyPropertyFlags.Unit)}
                               disabled={hasFlag(readOnlyFields, ResidencyPropertyFlags.Unit)}
                               validation={unitValidation.validation}
                               transform={unitValidation.transform}
                               minLength={unitValidation.minLength}
                               maxLength={unitValidation.maxLength}
                               label={translation(PermitTranslations.new_residency__apt_unit)}/>

                <MvvmTextInput context={context}
                               property="arguments.suffix"
                               type="text"
                               column="col-1of4"
                               hidden={hasFlag(hiddenFields, ResidencyPropertyFlags.Suffix)}
                               disabled={hasFlag(readOnlyFields, ResidencyPropertyFlags.Suffix)}
                               validation={suffixValidation.validation}
                               transform={suffixValidation.transform}
                               minLength={suffixValidation.minLength}
                               maxLength={suffixValidation.maxLength}
                               label={translation(PermitTranslations.new_residency__suffix)}/>

                <MvvmTextInput context={context}
                               property="arguments.address.postalCode"
                               type="text"
                               column="col-1of4"
                               autoComplete="postal-code"
                               hidden={hasFlag(hiddenFields, ResidencyPropertyFlags.PostalCode)}
                               disabled={hasFlag(readOnlyFields, ResidencyPropertyFlags.PostalCode)}
                               validation={postalCodeValidation.validation}
                               transform={postalCodeValidation.transform}
                               minLength={postalCodeValidation.minLength}
                               maxLength={postalCodeValidation.maxLength}
                               label={translation(PermitTranslations.new_residency__postal_code)}/>

				{freeFormCity
					? <MvvmTextInput context={context}
					                 property="arguments.address.city"
					                 type="text"
					                 column="col-1of4"
					                 autoComplete="address-level2"
					                 hidden={hasFlag(hiddenFields, ResidencyPropertyFlags.City)}
					                 disabled={hasFlag(readOnlyFields, ResidencyPropertyFlags.City)}
					                 validation={cityValidation.validation}
					                 transform={cityValidation.transform}
					                 minLength={cityValidation.minLength}
					                 maxLength={cityValidation.maxLength}
					                 label={translation(PermitTranslations.new_residency__city)}/>
					: <MvvmSelect context={context}
					              property="arguments.address.city"
					              items={cities}
					              placeholder={translation(PermitTranslations.validation__residency__city__placeholder)}
					              column="col-1of4"
					              hidden={hasFlag(hiddenFields, ResidencyPropertyFlags.City)}
					              disabled={hasFlag(readOnlyFields, ResidencyPropertyFlags.City)}
					              validation={cityValidation.validation}
					              label={translation(PermitTranslations.new_residency__city)}/>
				}

				{freeFormState
					? <MvvmTextInput context={context}
					                 property="arguments.address.state"
					                 type="text"
					                 column="col-1of4"
					                 autoComplete="address-level1"
					                 hidden={hasFlag(hiddenFields, ResidencyPropertyFlags.State)}
					                 disabled={hasFlag(readOnlyFields, ResidencyPropertyFlags.State)}
					                 validation={stateValidation.validation}
					                 transform={stateValidation.transform}
					                 minLength={stateValidation.minLength}
					                 maxLength={stateValidation.maxLength}
					                 label={translation(PermitTranslations.new_residency__state)}/>
					: <MvvmSelect context={context}
					              property="arguments.address.state"
					              items={provinces}
					              placeholder={translation(PermitTranslations.validation__residency__state__placeholder)}
					              column="col-1of4"
					              hidden={hasFlag(hiddenFields, ResidencyPropertyFlags.State)}
					              disabled={hasFlag(readOnlyFields, ResidencyPropertyFlags.State)}
					              validation={stateValidation.validation}
					              label={translation(PermitTranslations.new_residency__state)}/>
				}

                <MvvmSelect context={context}
                            property="arguments.zoneId"
                            items={zoneItems}
                            column="col-1of4"
                            placeholder={translation(PermitTranslations.validation__residency__zone__placeholder)}
                            hidden={hasFlag(hiddenFields, ResidencyPropertyFlags.Zone)}
                            disabled={hasFlag(readOnlyFields, ResidencyPropertyFlags.Zone)}
                            validation={zoneValidation.validation}
                            label={translation(PermitTranslations.zone)}/>
            </div>
		}
		{!hideBody && children}
		{!hideBody && <FullCreateAttachmentsComponent context={context} attachments={configuration.attachments}/>}
		{footer?.(context.state)}
	</div>);
}
